<template>
  <Card
    v-if="address"
    headless
    card-style="width:270px;"
    class="address-card md:!w-[270px]"
    :data-cy="`address-card-${address.id}`"
  >
    <template #body>
      <div
        class="d-flex flex-column justify-content-between full-height text-body"
      >
        <div>
          <div class="address-title-cont">
            <div class="tooltip-cont sera-flex flex-1">
              <div>
                <Tooltip v-if="isPinned" placement="top">
                  <template #trigger>
                    <i class="far fa-location-dot mr-1" />
                  </template>
                  <template #tooltip>
                    <span>Manually Pinned</span>
                  </template>
                </Tooltip>
                <span class="text-title">{{ title }}</span>
                <sera-button
                  v-if="!isHovered && title.length > MAX_CHARACTER_LIMIT"
                  class="ml-1 interactive"
                  label="more"
                  link
                  @click="toggleHovered"
                />
                <div v-if="isHovered">
                  <sera-button
                    class="interactive"
                    label="show less"
                    link
                    @click="toggleHovered"
                  />
                </div>
                <div v-if="!isBillingDelegated">
                  {{
                    address.primary ? " (Billing Address)" : "(Service Address)"
                  }}
                </div>
              </div>
              <div class="sera-ml-auto actions-menu" ignore-card-click>
                <ActionsMenu
                  placement="bottom-end"
                  width="50"
                  data-cy="actions-menu"
                  :actions="actions"
                  @edit-address="emit('edit-address', address)"
                  @edit-taxes="emit('edit-tax-settings', address)"
                >
                  <template #trigger>
                    <div class="more-actions" ignore-card-click>
                      <i class="fas fa-ellipsis-v text-blue"></i>
                    </div>
                  </template>
                </ActionsMenu>
              </div>
            </div>
          </div>
          <AddressLabel :address="address" location-type label="Address:" />
          <div class="tax-details mt-2">
            <AddressTaxLabel class="mt-2" :address="address" />
          </div>
        </div>
        <div v-if="canAccess('jobs')" class="schedule-container mt-2">
          <Tooltip v-if="isManualEntry" placement="top">
            <template #trigger>
              <div class="d-flex align-items-center">
                <sera-button
                  ghost
                  iconRight
                  :icon="'circle-info'"
                  class="ml-auto"
                  :label="scheduleText"
                  :disabled="isArchivedOrManualEntry"
                  data-cy="address-schedule-link"
                  @click="handleSchedule"
                />
              </div>
            </template>
            <template #tooltip>
              <div class="text-center">
                Unverified address. Update address or pin location to enable
                scheduling.
              </div>
            </template>
          </Tooltip>

          <sera-button
            v-else
            ghost
            iconRight
            icon="calendar-plus"
            class="ml-auto"
            :label="scheduleText"
            :disabled="isArchivedOrManualEntry"
            data-cy="address-schedule-link"
            @click="handleSchedule"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref } from "vue";
import { useStore } from "vuex";
import { useAccessRole } from "@/lib/useAccessRole";

import ActionsMenu from "@/components/ActionsMenu.vue";
import AddressLabel from "@/components/AddressLabel.vue";
import AddressTaxLabel from "@/components/AddressTaxLabel.vue";
import Card from "@/components/Card.vue";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";

const MAX_CHARACTER_LIMIT = 50;

const props = defineProps<{
  address: Address;
}>();
const emit = defineEmits(["schedule", "edit-address", "edit-tax-settings"]);
const store = useStore();
const isHovered = ref(false);
const { canAccess } = useAccessRole();

const currentUser = computed(() => {
  if (!store.state.currentUser) {
    return null;
  }

  return new Customer(store.state.currentUser);
});
const title = computed(() => {
  const title = props.address.name;
  return !isHovered.value && title.length > MAX_CHARACTER_LIMIT
    ? `${title.substring(0, MAX_CHARACTER_LIMIT)}...`
    : title;
});
const isManualEntry = computed(() => props.address.manual_entry);
const isArchivedOrManualEntry = computed(
  () => currentUser.value!.archived || props.address.manual_entry
);
const isPinned = computed(() => props.address.pinned);
const isBillingDelegated = computed(() => currentUser.value!.delegate_billing);
const scheduleText = computed(() => {
  return isArchivedOrManualEntry.value ? "Unable to Schedule" : "Schedule";
});
const actions = computed(() => {
  return [
    {
      label: "Edit Information",
      icon: "fas fa-edit",
      eventName: "edit-address"
    },
    {
      label: "Edit Taxes",
      icon: "fas fa-badge-percent",
      eventName: "edit-taxes"
    }
  ];
});

const toggleHovered = () => {
  isHovered.value = !isHovered.value;
};

const handleSchedule = () => {
  if (!isArchivedOrManualEntry.value) {
    emit("schedule", props.address);
  }
};

defineExpose({
  MAX_CHARACTER_LIMIT
});
</script>

<style lang="scss" scoped>
.address-card {
  :deep(.card-body) {
    padding: 16px !important;
  }
  &.clickable {
    &:hover {
      background: var(--sera-color-core-blue-light-blue-50);
      border-color: var(--sera-color-core-blue-light-blue-300);
      box-shadow: 0 0 0 2px var(--sera-color-core-blue-light-blue-300) inset,
        0px 4px 10px 0px rgba(0, 0, 0, 0.15) !important;
      cursor: pointer;
      .schedule-container {
        background: var(--sera-color-core-blue-light-blue-100);
      }
      .more-actions {
        background: var(--sera-color-core-blue-light-blue-200);
      }
    }
  }
}
.address-title-cont {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid var(--sera-color-neutral-gray-med-gray-500);
  padding-bottom: 8px;
  margin-bottom: 12px;
  .actions-menu :deep(.tooltip-trigger) {
    text-align: right;
  }
}

.schedule-container {
  display: flex;
  padding: 10px;
  background: var(--sera-color-neutral-gray-light-gray-100);
}
.tax-details {
  margin-top: auto;
}
.more-actions {
  background: white;
  width: 20px;
  cursor: pointer;
  height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  &:hover {
    background: var(--sera-color-core-blue-light-blue-100);
  }
}
</style>
